import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import mammoth from 'mammoth/mammoth.browser';
import * as XLSX from 'xlsx';
import DOMPurify from 'dompurify';
import { Download, X, FileText } from 'lucide-react';
import { ModalSkeleton } from './Skeleton';

const AUTH_TOKEN_KEY = 'authToken';

function getAuthToken() {
  try {
    return localStorage.getItem(AUTH_TOKEN_KEY) || '';
  } catch (_) {
    return '';
  }
}

/** Authentifizierter Fetch — Anhang-APIs brauchen Bearer-Token. */
export async function authFetch(url, options = {}) {
  const headers = new Headers(options.headers || {});
  const token = getAuthToken();
  if (token && !headers.has('Authorization')) {
    headers.set('Authorization', `Bearer ${token}`);
  }
  return fetch(url, {
    ...options,
    headers,
    credentials: options.credentials || 'same-origin',
  });
}

/** Datei mit Auth laden und als Download speichern. */
export async function downloadAuthenticated(url, filename = 'download') {
  const response = await authFetch(url);
  if (!response.ok) {
    const text = await response.text().catch(() => '');
    throw new Error(text || `Download fehlgeschlagen (${response.status})`);
  }
  const blob = await response.blob();
  const objectUrl = URL.createObjectURL(blob);
  try {
    const a = document.createElement('a');
    a.href = objectUrl;
    a.download = filename || 'download';
    a.rel = 'noopener';
    document.body.appendChild(a);
    a.click();
    a.remove();
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}

export function getPreviewKind(filename = '', contentType = '') {
  const name = String(filename).toLowerCase();
  const type = String(contentType).toLowerCase();

  if (type.startsWith('image/') || /\.(png|jpe?g|gif|webp|bmp|svg)$/i.test(name)) return 'image';
  if (type === 'application/pdf' || name.endsWith('.pdf')) return 'pdf';
  if (
    type.includes('wordprocessingml') ||
    type === 'application/vnd.ms-word.document.macroenabled.12' ||
    name.endsWith('.docx')
  ) return 'docx';
  if (type === 'application/msword' || name.endsWith('.doc')) return 'doc';
  if (
    type.includes('spreadsheetml') ||
    type.includes('excel') ||
    type === 'text/csv' ||
    /\.(xlsx|xls|csv)$/i.test(name)
  ) return 'excel';
  if (type.startsWith('text/') || /\.(txt|md|json|xml|log|js|css|html?|csv)$/i.test(name)) return 'text';
  if (type.startsWith('video/') || /\.(mp4|webm|ogg|mov)$/i.test(name)) return 'video';
  if (type.startsWith('audio/') || /\.(mp3|wav|ogg|m4a)$/i.test(name)) return 'audio';
  return 'other';
}

async function loadPreviewFromBlob(blob, filename, contentType) {
  const kind = getPreviewKind(filename, contentType || blob?.type || '');
  const objectUrl = URL.createObjectURL(blob);
  const buffer = await blob.arrayBuffer();

  if (kind === 'image' || kind === 'pdf' || kind === 'video' || kind === 'audio') {
    return { kind, objectUrl };
  }

  if (kind === 'text') {
    const text = await blob.text();
    return { kind, objectUrl, text };
  }

  if (kind === 'docx') {
    const result = await mammoth.convertToHtml({ arrayBuffer: buffer });
    return {
      kind,
      objectUrl,
      html: DOMPurify.sanitize(result.value || '<p>Kein Inhalt</p>', { USE_PROFILES: { html: true } }),
    };
  }

  if (kind === 'excel') {
    const workbook = XLSX.read(buffer, { type: 'array' });
    const sheets = workbook.SheetNames.map((name) => {
      const sheet = workbook.Sheets[name];
      const html = XLSX.utils.sheet_to_html(sheet, { editable: false });
      return {
        name,
        html: DOMPurify.sanitize(html, {
          USE_PROFILES: { html: true },
          ADD_TAGS: ['table', 'thead', 'tbody', 'tr', 'th', 'td', 'colgroup', 'col'],
        }),
      };
    });
    return { kind, objectUrl, sheets, activeSheet: 0 };
  }

  if (kind === 'doc') {
    return {
      kind: 'unsupported',
      objectUrl,
      message:
        'Alte .doc-Dateien können im Browser nicht zuverlässig angezeigt werden. Bitte herunterladen oder als .docx speichern.',
    };
  }

  return {
    kind: 'other',
    objectUrl,
    message: 'Für diesen Dateityp gibt es keine Inline-Vorschau.',
  };
}

async function loadPreviewContent(url, filename, contentType) {
  const response = await authFetch(url);
  if (!response.ok) {
    if (response.status === 401) throw new Error('Nicht angemeldet — bitte neu einloggen');
    if (response.status === 404) throw new Error('Anhang nicht gefunden');
    throw new Error(`Anhang konnte nicht geladen werden (${response.status})`);
  }
  const blob = await response.blob();
  return loadPreviewFromBlob(blob, filename, contentType);
}

function downloadLocalBlob(blob, filename = 'download') {
  const objectUrl = URL.createObjectURL(blob);
  try {
    const a = document.createElement('a');
    a.href = objectUrl;
    a.download = filename || 'download';
    a.rel = 'noopener';
    document.body.appendChild(a);
    a.click();
    a.remove();
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}

const AttachmentPreviewModal = ({ attachment, onClose }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [content, setContent] = useState(null);
  const [activeSheet, setActiveSheet] = useState(0);
  const [downloading, setDownloading] = useState(false);

  const isLocalFile =
    typeof File !== 'undefined' &&
    attachment?.file &&
    (attachment.file instanceof File || attachment.file instanceof Blob);

  useEffect(() => {
    let cancelled = false;
    let objectUrl = null;

    const run = async () => {
      setLoading(true);
      setError('');
      setContent(null);
      setActiveSheet(0);
      try {
        let result;
        if (isLocalFile) {
          result = await loadPreviewFromBlob(
            attachment.file,
            attachment.filename || attachment.file.name || 'Anhang',
            attachment.contentType || attachment.file.type || ''
          );
        } else {
          const rawUrl = String(attachment.url || '');
          if (!rawUrl) throw new Error('Keine Vorschau-URL');
          const previewUrl = rawUrl.includes('inline=1')
            ? rawUrl
            : `${rawUrl}${rawUrl.includes('?') ? '&' : '?'}inline=1`;
          result = await loadPreviewContent(
            previewUrl,
            attachment.filename,
            attachment.contentType
          );
        }
        if (cancelled) {
          if (result.objectUrl) URL.revokeObjectURL(result.objectUrl);
          return;
        }
        objectUrl = result.objectUrl;
        setContent(result);
      } catch (err) {
        if (!cancelled) setError(err.message || 'Vorschau fehlgeschlagen');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    run();

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [attachment, isLocalFile]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const handleDownload = async () => {
    if (downloading) return;
    setDownloading(true);
    try {
      if (isLocalFile) {
        downloadLocalBlob(
          attachment.file,
          attachment.filename || attachment.file.name || 'download'
        );
      } else {
        const href = attachment.downloadUrl || attachment.url;
        await downloadAuthenticated(href, attachment.filename || 'download');
      }
    } catch (err) {
      setError(err.message || 'Download fehlgeschlagen');
    } finally {
      setDownloading(false);
    }
  };

  return createPortal(
    <div
      className="fixed inset-0 z-[250] flex items-center justify-center p-3 sm:p-6 bg-neutral-900/75 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="bg-white dark:bg-neutral-900 rounded-2xl shadow-2xl w-full max-w-5xl max-h-[92vh] flex flex-col overflow-hidden border border-neutral-200 dark:border-neutral-800"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-4 py-3 border-b border-neutral-200 dark:border-neutral-800 flex items-center justify-between gap-3 flex-shrink-0">
          <div className="min-w-0 flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg bg-neutral-100 dark:bg-neutral-800 flex items-center justify-center flex-shrink-0">
              <FileText className="w-4 h-4 text-neutral-500" />
            </div>
            <div className="min-w-0">
              <p className="text-sm font-semibold text-neutral-900 dark:text-white truncate">{attachment.filename}</p>
              <p className="text-[11px] text-neutral-500 truncate">{attachment.contentType}</p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <button
              type="button"
              onClick={handleDownload}
              disabled={downloading}
              className="btn-secondary !py-1.5 !px-3 text-xs disabled:opacity-60"
            >
              <Download className="w-3.5 h-3.5 mr-1.5" />
              {downloading ? '…' : 'Download'}
            </button>
            <button onClick={onClose} className="btn-ghost p-2" aria-label="Schließen">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {content?.kind === 'excel' && content.sheets?.length > 1 && (
          <div className="px-4 py-2 border-b border-neutral-100 dark:border-neutral-800 flex gap-1 overflow-x-auto flex-shrink-0 bg-neutral-50 dark:bg-neutral-900/80">
            {content.sheets.map((sheet, idx) => (
              <button
                key={sheet.name}
                type="button"
                onClick={() => setActiveSheet(idx)}
                className={`px-2.5 py-1 rounded-md text-[11px] font-medium whitespace-nowrap ${
                  activeSheet === idx
                    ? 'bg-white dark:bg-neutral-800 text-neutral-900 dark:text-white shadow-sm border border-neutral-200 dark:border-neutral-700'
                    : 'text-neutral-500 hover:text-neutral-800 dark:hover:text-neutral-200'
                }`}
              >
                {sheet.name}
              </button>
            ))}
          </div>
        )}

        <div className="flex-1 min-h-0 overflow-auto bg-neutral-100 dark:bg-neutral-950">
          {loading ? (
            <div className="p-5 min-h-[40vh]">
              <ModalSkeleton lines={6} />
            </div>
          ) : error ? (
            <div className="h-full min-h-[40vh] flex flex-col items-center justify-center gap-3 text-center px-6">
              <p className="text-sm text-danger-600">{error}</p>
              <button type="button" onClick={handleDownload} className="btn-primary inline-flex" disabled={downloading}>
                <Download className="w-4 h-4 mr-1.5" />
                Trotzdem herunterladen
              </button>
            </div>
          ) : content?.kind === 'image' ? (
            <div className="h-full min-h-[40vh] flex items-center justify-center p-4">
              <img src={content.objectUrl} alt={attachment.filename} className="max-w-full max-h-[75vh] object-contain rounded-lg shadow" />
            </div>
          ) : content?.kind === 'pdf' ? (
            <iframe title={attachment.filename} src={content.objectUrl} className="w-full h-[75vh] border-0 bg-white" />
          ) : content?.kind === 'video' ? (
            <div className="h-full min-h-[40vh] flex items-center justify-center p-4">
              <video src={content.objectUrl} controls className="max-w-full max-h-[75vh] rounded-lg" />
            </div>
          ) : content?.kind === 'audio' ? (
            <div className="h-full min-h-[40vh] flex items-center justify-center p-8">
              <audio src={content.objectUrl} controls className="w-full max-w-lg" />
            </div>
          ) : content?.kind === 'text' ? (
            <pre className="p-4 text-xs sm:text-sm whitespace-pre-wrap break-words text-neutral-800 dark:text-neutral-200 font-mono">
              {content.text}
            </pre>
          ) : content?.kind === 'docx' ? (
            <div
              className="p-5 prose prose-sm dark:prose-invert max-w-none bg-white dark:bg-neutral-900 min-h-[40vh]"
              dangerouslySetInnerHTML={{ __html: content.html }}
            />
          ) : content?.kind === 'excel' ? (
            <div
              className="p-3 overflow-auto attachment-excel-preview bg-white dark:bg-neutral-900 min-h-[40vh]"
              dangerouslySetInnerHTML={{ __html: content.sheets?.[activeSheet]?.html || '' }}
            />
          ) : (
            <div className="h-full min-h-[40vh] flex flex-col items-center justify-center gap-3 text-center px-6">
              <p className="text-sm text-neutral-600 dark:text-neutral-300">
                {content?.message || 'Keine Vorschau verfügbar.'}
              </p>
              <button type="button" onClick={handleDownload} className="btn-primary inline-flex" disabled={downloading}>
                <Download className="w-4 h-4 mr-1.5" />
                Herunterladen
              </button>
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default AttachmentPreviewModal;
